import styled from 'styled-components';

export type LegendEntry = {
  key: string;
  name: string;
  color: string;
  // Drawn dashed: the palette has wrapped around.
  dashed: boolean;
};

type ChartLegendProps = {
  entries: LegendEntry[];
  // The keys of the lines currently hidden; clicking an entry toggles it.
  hidden: ReadonlySet<string>;
  onToggle: (key: string) => void;
};

// The chart's legend, rendered under the plot instead of recharts' own
// <Legend>: that one is absolutely positioned inside the fixed-height chart,
// so a long one (a team of twenty handlers) stacks up over the plot and out
// of the card. This one wraps and makes the card taller instead.
//
// Duplicated in the claims webapp
// (apps/claims-webapp/src/pages/Reports/components/ChartLegend.tsx): the two
// packages share no code.
const ChartLegend = ({ entries, hidden, onToggle }: ChartLegendProps) => (
  <List aria-label='Legend'>
    {entries.map((entry) => (
      <li key={entry.key}>
        <EntryButton
          type='button'
          aria-pressed={!hidden.has(entry.key)}
          onClick={() => onToggle(entry.key)}
        >
          <Swatch
            style={{
              borderTopColor: entry.color,
              borderTopStyle: entry.dashed ? 'dashed' : 'solid',
            }}
          />
          {entry.name}
        </EntryButton>
      </li>
    ))}
  </List>
);

export default ChartLegend;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.25rem 1rem;
  font-size: 0.85rem;
`;

const EntryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &[aria-pressed='false'] {
    opacity: 0.4;
  }
`;

const Swatch = styled.span`
  flex: none;
  width: 1rem;
  border-top: 3px solid;
  border-radius: 2px;
`;
